import React, { useEffect, useState } from "react";
import { Plus, FolderKanban, Trash2, X, Users, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminProjects() {
  const [rows, setRows] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", page_count: 500 });

  const load = async () => {
    try {
      const r = await api.get("/admin/projects");
      setRows(r.data || []);
    } catch (e) { toast.error("Failed to load projects"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ name: "", page_count: 500 }); setShowCreate(true); };

  const submit = async () => {
    if (saving) return;
    const name = (form.name || "").trim();
    const pc = Number(form.page_count);
    if (!name) { toast.error("Name required"); return; }
    if (!pc || pc < 1 || pc > 2000) { toast.error("Page count must be 1 – 2000"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/projects", { name, page_count: pc });
      toast.success(`${r.data.name} created with ${r.data.page_count} unique pages`);
      setShowCreate(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (p.id === "project_1") { toast.error("Cannot delete Project 1 (default)"); return; }
    if (p.employee_count > 0) { toast.error(`${p.employee_count} employee(s) still on this project — reassign them first`); return; }
    if (!window.confirm(`Delete "${p.name}" and its ${p.actual_page_count} pages? This is permanent.`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!rows) return <div className="text-zinc-500">Loading…</div>;

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Projects</h1>
          <p className="text-zinc-500 mt-2 max-w-2xl text-sm">
            Each project has its own unique set of typing pages. Assign candidates to different projects so their content is always fresh.
          </p>
        </div>
        <button
          data-testid="new-project-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ink)] text-white text-sm font-semibold hover:bg-[var(--accent)]"
        >
          <Plus size={14} /> New Project
        </button>
      </div>

      <div className="mt-6 grid gap-3">
        {rows.length === 0 && <div className="text-zinc-500">No projects yet.</div>}
        {rows.map((p) => (
          <div
            key={p.id}
            data-testid={`project-row-${p.id}`}
            className="card-clean p-5 flex items-center justify-between gap-4 flex-wrap"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                <FolderKanban size={20} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-lg truncate">{p.name}</div>
                <div className="text-xs text-zinc-500 font-mono mt-0.5">{p.id}</div>
              </div>
            </div>
            <div className="flex items-center gap-5 text-sm">
              <span className="inline-flex items-center gap-1.5 text-zinc-700"><FileText size={14} className="text-zinc-400" /> <b className="tabular-nums">{p.actual_page_count}</b> pages</span>
              <span className="inline-flex items-center gap-1.5 text-zinc-700"><Users size={14} className="text-zinc-400" /> <b className="tabular-nums">{p.employee_count}</b> employees</span>
              <button
                data-testid={`delete-project-${p.id}`}
                onClick={() => remove(p)}
                disabled={p.id === "project_1" || p.employee_count > 0}
                title={p.id === "project_1" ? "Default project" : (p.employee_count > 0 ? "Employees assigned" : "Delete project")}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-200 text-zinc-600 text-xs font-semibold hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-zinc-600 disabled:cursor-not-allowed"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold">New Project</h2>
              <button onClick={() => setShowCreate(false)} className="text-zinc-500 hover:text-zinc-800"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Project Name</label>
                <input
                  data-testid="new-project-name"
                  autoFocus
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Project 2, Client XYZ Batch"
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-[var(--line)] focus:border-[var(--accent)] outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Page Count</label>
                <input
                  data-testid="new-project-page-count"
                  type="number"
                  min="1"
                  max="2000"
                  value={form.page_count}
                  onChange={(e) => setForm({ ...form, page_count: e.target.value })}
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-[var(--line)] focus:border-[var(--accent)] outline-none"
                />
                <div className="text-[11px] text-zinc-500 mt-1">Each page will be auto-generated with unique random encrypted content. This takes a few seconds per 500 pages.</div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
              <button
                data-testid="new-project-save"
                onClick={submit}
                disabled={saving}
                className="btn-primary disabled:opacity-50"
              >
                {saving ? "Creating…" : "Create Project"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
